var AnimationSupport = require("enyo-anim/AnimationSupport"),
    Animator         = require("enyo-anim/Animator"),
    Attractor        = require("enyo-anim/Attractor"),
    PerformanceMode  = require("enyo-perf/PerformanceMode"),
    Scalar           = require("enyo-anim/Scalar"),
    kind             = require("enyo/kind");

require("enyo-ui");

/**
 * [FadeSupport]{@link enyo-ui.FadeSupport} implements fade-in and fade-out
 * animations for a Control, using the `enyo-anim` library.
 *
 * @mixin enyo-ui.FadeSupport
 * @mixes enyo-anim.AnimationSupport
 * @public
 */
module.exports = {

    /**
     * @private
     */
    name: "enyo-ui.FadeSupport",

    /**
     * @private
     */
    mixins: [AnimationSupport],

    /**
     * Whether the control is currently fading.
     *
     * @type     {boolean}
     * @readonly
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    fading: false,

    /**
     * Whether to switch to performance mode during fade animation. This value
     * should not be toggled during a fade.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    usePerformanceMode: false,

    /**
     * The initial opacity value at instantiation time.
     *
     * @name     initialOpacity
     * @type     {number}
     * @default  1
     * @memberof enyo-ui.FadeSupport
     * @public
     */

    /**
     * Init opacity animator.
     *
     * @memberof enyo-ui.FadeSupport
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.opacity = new Animator({
                scalar: new Scalar({
                    value:   this.initialOpacity,
                    epsilon: 0.01
                }),
                attractors: {
                    target: new Attractor({
                        attraction: 0.02
                    })
                }
            });
            sup.apply(this, arguments);
        };
    }),

    /**
     * @memberof enyo-ui.FadeSupport
     * @method
     * @private
     */
    constructed: kind.inherit(function(sup) {
        return function() {
            if (typeof this.initialOpacity !== "number") {
                this.initialOpacity = 1;
            }
            this.updateFade = this.bindSafely(this.updateFade);
            sup.apply(this, arguments);
        };
    }),

    /**
     * @memberof enyo-ui.FadeSupport
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.applyFade();
        };
    }),

    /**
     * A fading animation may still be active. We need to finish it, so it can
     * safely release performanceMode.
     *
     * @memberof enyo-ui.FadeSupport
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.fading && this.stopFade();
            sup.apply(this, arguments);
        };
    }),

    // ...........................
    // Fade implementation

    /**
     * Starts a fade animation.
     *
     * @memberof enyo-ui.FadeSupport
     * @private
     */
    startFade: function() {
        if (this.fading) {
            return;
        }

        this.usePerformanceMode && PerformanceMode.enable();
        this.set("fading", true);
        this.updateFade();
    },

    /**
     * Update opacity animator, apply opacity, schedule next animation frame as
     * needed.
     *
     * @memberof enyo-ui.FadeSupport
     * @private
     */
    updateFade: function() {
        var opacity = this.opacity;

        // Only during fade
        if (!this.fading) {
            this.warn("Don't call `updateFade` if control is not fading.");

            return;
        }

        // Update opacity
        opacity.update();

        // Animation is finished
        if (opacity.isDone()) {
            opacity.finish();
            this.applyFade();
            this.stopFade();
        }

        // Animation needs another frame
        else {
            this.applyFade();
            this.requestAnimationFrame("updateFade", this.updateFade);
        }
    },

    /**
     * Applies the current fade to the DOM.
     *
     * @memberof enyo-ui.FadeSupport
     * @private
     */
    applyFade: function() {
        var animator       = this.opacity,
            opacity        = animator.scalar.value,
            clippedOpacity = Math.max(0, Math.min(1, opacity));

        // Apply styles to DOM
        this.applyStyle("opacity", clippedOpacity);

        // Hide if needed
        if (this.showing && this.willHide && opacity <= 0
            && (!this.fading || !animator.isMoving())
        ) {
            this.set("showing", false);
        }

        // Show if needed
        else if (!this.showing && this.willShow && opacity > 0) {
            this.set("showing", true);
        }
    },

    /**
     * Snaps transition to target and stops.
     *
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    snapFade: function() {
        this.opacity.snapToAttractor("target");
        this.applyFade();
        this.fading && this.stopFade();
    },

    /**
     * Stops a fade animation.
     *
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    stopFade: function() {
        if (!this.fading) {
            return;
        }

        this.opacity.stop();
        this.cancelAnimationFrame("updateFade");
        this.usePerformanceMode && PerformanceMode.disable();
        this.set("fading", false);
    },

    // ...........................
    // Show / Hide

    /**
     * Show the control, optionally fade the control too.
     *
     * @param    {boolean=}  fade  `true` if control should fade, `false`
     *                             otherwise
     * @param    {boolean=}  snap  `true` if fade should be instant, `false`
     *                             otherwise
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    show: function(fade, snap) {
        if (fade === true) {
            !this.showing && !this.willShow && this.set("willShow", true);
            this.fadeIn(snap);
        }
        else {
            !this.showing && this.set("showing", true);
        }
    },

    /**
     * Hide the control, optionally fade the control too.
     *
     * @param    {boolean=}  fade  `true` if control should fade, `false`
     *                             otherwise
     * @param    {boolean=}  snap  `true` if fade should be instant, `false`
     *                             otherwise
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    hide: function(fade, snap) {
        if (fade === true) {
            this.showing && !this.willHide && this.set("willHide", true);
            this.fadeOut(snap);
        }
        else {
            this.showing && this.set("showing", false);
        }
    },

    /**
     * Unsets `willHide` and `willShow` indicators when showing state changes.
     *
     * @memberof enyo-ui.FadeSupport
     * @method
     * @private
     */
    showingChanged: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.willHide && this.set("willHide", false);
            this.willShow && this.set("willShow", false);
        };
    }),

    // ...........................
    // Faders

    /**
     * Fade to specific opacity:
     *  - 0 is fully transparent
     *  - 1 is fully opaque
     *
     * @param    {number}    opacity  The opacity
     * @param    {boolean=}  snap     Whether to snap instead of animate
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    fadeTo: function(opacity, snap) {
        var scalar    = this.opacity.scalar,
            target    = this.opacity.attractors.target,
            newTarget = Math.max(0, Math.min(1, opacity));


        if (target.value !== newTarget) {
            target.value = newTarget;
        }

        // Control is no longer going to end up hiding
        if (target.value > 0 && this.willHide) {
            this.set("willHide", false);
        }

        // Control is no longer going to end up showing
        if (target.value === 0 && this.willShow) {
            this.set("willShow", false);
        }

        // Ensure the fade is properly applied
        if (scalar.value === target.value) {
            this.applyFade();
        }
        else if (snap) {
            this.snapFade();
        }
        else {
            !this.fading && this.startFade();
        }
    },

    /**
     * Fade in to full opacity.
     *
     * @param    {boolean=}  snap  Whether to snap instead of animate
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    fadeIn: function(snap) {
        this.fadeTo(1, snap);
    },

    /**
     * Fade out to full transparency.
     *
     * @param    {boolean=}  snap  Whether to snap instead of animate
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    fadeOut: function(snap) {
        this.fadeTo(0, snap);
    },

    // ...........................
    // Issers

    /**
     * Determines if control is fading.
     *
     * @return   {boolean}  `true` if fading, `false` otherwise.
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    isFading: function() {
        return this.fading;
    },

    /**
     * Determines if control is fading to the given opacity.
     *
     * @param    {number}   opacity  The opacity
     * @return   {boolean}  `true` if faded to, `false` otherwise.
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    isFadingTo: function(opacity) {
        return this.isFading() && this.opacity.attractors.target.value === opacity;
    },

    /**
     * Determines if control is fading out.
     *
     * @return   {boolean}  `true` if fading out, `false` otherwise.
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    isFadingOut: function() {
        return this.isFadingTo(0);
    },

    /**
     * Determines if control is fading in.
     *
     * @return   {boolean}  `true` if fading in, `false` otherwise.
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    isFadingIn: function() {
        return this.isFadingTo(1);
    },

    /**
     * Determines if control is faded to the given opacity.
     *
     * @param    {number}   opacity  The opacity
     * @return   {boolean}  `true` if faded to, `false` otherwise.
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    isFadedTo: function(opacity) {
        return !this.isFading() && this.opacity.scalar.value === opacity;
    },

    /**
     * Determines if control is fully faded in.
     *
     * @return   {boolean}  `true` if faded in, `false` otherwise.
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    isFadedIn: function() {
        return this.isFadedTo(1);
    },

    /**
     * Determines if control is fully faded out.
     *
     * @return   {boolean}  `true` if faded out, `false` otherwise.
     * @memberof enyo-ui.FadeSupport
     * @public
     */
    isFadedOut: function() {
        return this.isFadedTo(0);
    }

};
